import React from 'react';
import { sumBy, uniqBy } from 'lodash';
import {
	CourseEntity, ProgramRuleEntity, RequirementEntity, StudyPlanEntity,
} from 'Models/Entities';
import {
	DuplicateCourseItem,
} from 'Views/Pages/Frontend/StudyPlanWizard/RequirementFulfilmentCheck/DuplicateCourseSelection';
import { DataIndex } from 'Util/DataIndexUtils';
import { RequirementNode } from 'Views/Pages/Frontend/StudyPlanWizard/RequirementTreeGeneration';

interface useUnitSelectedProps {
	requirementTree: Record<string, RequirementNode>,
	studyPlan?: StudyPlanEntity,
	coursesById: DataIndex<CourseEntity>,
	duplicateCourses: DuplicateCourseItem[],
}

const useUnitSelected = (props: useUnitSelectedProps) => {
	const {
		requirementTree,
		studyPlan,
		coursesById,
		duplicateCourses,
	} = props;

	const distinctIncompletePlanSelections = uniqBy(
		(studyPlan?.getIncompletePlanSelections() || []),
		planSelection => planSelection.courseId,
	);

	const distinctCompletedPlanSelections = uniqBy(
		(studyPlan?.getCompletedPlanSelections() || []),
		planSelection => planSelection.courseId,
	);

	// unit count calculations
	// get incomplete and complete selectinos, and remove any duplicates
	// for the same course
	const selectedUnitCount = sumBy(
		distinctIncompletePlanSelections,
		planSelection => coursesById[planSelection.courseId]?.units || 0,
	);

	const completedUnitCount = sumBy(
		distinctCompletedPlanSelections,
		planSelection => coursesById[planSelection.courseId]?.units || 0,
	);

	const totalUnitsSelected = completedUnitCount + selectedUnitCount;
	const duplicateUnitsSelected = sumBy(duplicateCourses, duplicateCourse => duplicateCourse.course.units);

	const requiredTotalUnitCondition = studyPlan?.programVersion.rootProgramRules?.[0]?.getUnitMinimum();

	const isProgramTotalConditionNotMet = (
		(requiredTotalUnitCondition?.ruleType === 'EQUAL' && requiredTotalUnitCondition.units !== totalUnitsSelected)
		|| (requiredTotalUnitCondition?.ruleType === 'MIN' && requiredTotalUnitCondition.units < totalUnitsSelected)
	);

	const displayingRequirements: {
		requirementNode: RequirementNode,
		requirement: RequirementEntity | ProgramRuleEntity
	}[] = Object.values(requirementTree)
		.map(requirementNode => {
			/*
				If component is an AND condition (contains multiple requirements)
				display its name.

				If component is an OR condition (contains a single selected
				requirement), display the selected requirement's name
			*/
			const displayTopLevelRequirementName = (
				requirementNode.requirement.condition === 'OR'
				&& requirementNode.childNodes.length <= 1
			);

			return {
				requirementNode,
				requirement: displayTopLevelRequirementName
					? requirementNode.childNodes[0].requirement
					: requirementNode.requirement,
			};
		});

	return {
		displayingRequirements,
		totalUnitsSelected,
		duplicateUnitsSelected,
		isProgramTotalConditionNotMet,
		requiredTotalUnitCondition,
	};
};

export default useUnitSelected;
